// src/components/common/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";
import {
  setUser,
  setAuthenticated,
  setLoading,
} from "../../app/slice/authSlice";

const ProtectedRoute = ({ children, requiredPermissions = [] }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        if (user) {
          dispatch(
            setUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              emailVerified: user.emailVerified,
            }),
          );
          dispatch(setAuthenticated(true));
        } else {
          dispatch(setAuthenticated(false));
          dispatch(setUser(null));
        }
        setCheckingAuth(false);
        dispatch(setLoading(false));
      },
      (error) => {
        console.error("Auth state error:", error);
        setCheckingAuth(false);
        dispatch(setLoading(false));
        dispatch(setAuthenticated(false));
      },
    );

    return () => unsubscribe();
  }, [dispatch]);

  if (checkingAuth || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {checkingAuth ? "Checking authentication..." : "Loading..."}
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
